import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import cookieService from "../services/cookieService";

type GTMEventParams = Record<string, unknown>;

interface GTMEvent {
  event: string;
  [key: string]: unknown;
}

const isGTMEnabled = (): boolean => {
  return Boolean(window._env_?.REACT_APP_GTM_ID);
};

const PAGE_VIEW_EVENT = "page_view";

/**
 * Query parameters that never go to analytics in page_view.page_search:
 * - `subdomain` — tenant identity (AC-11);
 * - `invite` — single-use trial-invite bearer token (review MEDIUM-2 of
 *   2026-09-30-trial-invite-bez-proformy). public/invite-capture.js strips it
 *   from the URL before GTM loads; this is the second line of defence.
 */
const PAGE_SEARCH_DENYLIST = ["subdomain", "invite"] as const;

/**
 * Removes the PAGE_SEARCH_DENYLIST query parameters from a search string.
 * Inline reimplementation — avoids circular dependency with useRegistrationAnalytics.
 */
const sanitizePageSearch = (search: string): string => {
  if (!search || !PAGE_SEARCH_DENYLIST.some((name) => search.includes(name))) {
    return search;
  }
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    PAGE_SEARCH_DENYLIST.forEach((name) => params.delete(name));
    const sanitized = params.toString();
    return sanitized ? `?${sanitized}` : "";
  } catch {
    return search;
  }
};

const pushToDataLayer = (data: GTMEvent): void => {
  if (!isGTMEnabled()) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[GTM] GTM not enabled (REACT_APP_GTM_ID missing). Event "${data.event}" was not pushed.`,
        "Check window._env_.REACT_APP_GTM_ID configuration."
      );
    }
    return;
  }

  // page_view is always allowed (essential analytics)
  const isPageView = data.event === PAGE_VIEW_EVENT;

  // Gate on ANALYTICS consent, not marketing. Every event pushed through here is a
  // GA4 analytics/engagement event (registration funnel, sign_up conversion, etc.),
  // governed by analytics_storage under GA4 Consent Mode — not advertising.
  // Ad-platform tags (Meta Pixel/CAPI) are gated independently at the GTM tag level
  // by ad_storage, so loosening this gate does not send anything to ad platforms.
  // Gating analytics events on marketing consent silently dropped the sign_up
  // conversion for users who accepted analytics but declined marketing.
  //
  // UNDECIDED consent (no banner choice yet) is NOT a block: gtm-loader.js only
  // loads the GTM container after the user grants analytics/marketing, so pushes
  // made before the decision sit inertly in window.dataLayer (no network traffic).
  // If the user accepts in-session, GTM loads and replays the queued messages in
  // order — this fixes the mount-race that silently dropped registration_start
  // (fired on /register mount, before the banner was answered, and never retried:
  // GA4 showed 6 registration_start users vs 19 page_view users on /register).
  // If the user declines, GTM never loads and the queued messages are never sent.
  // Only an EXPLICIT analytics refusal blocks the push.
  const consentDecided = cookieService.hasConsent();
  if (!isPageView && consentDecided && !cookieService.hasAnalyticsConsent()) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[GTM] Event "${data.event}" blocked — analytics consent not granted.`,
        "User must accept analytics cookies to enable tracking."
      );
    }
    return;
  }

  // Sanitize page_search for page_view events: no ?subdomain= (AC-11), no ?invite= token
  const eventData: GTMEvent =
    isPageView && typeof data.page_search === "string"
      ? { ...data, page_search: sanitizePageSearch(data.page_search) }
      : data;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);
};

/**
 * Get GA4 client_id from _ga cookie.
 * Format: GA1.1.XXXXXXXXXX.YYYYYYYYYY -> returns XXXXXXXXXX.YYYYYYYYYY
 *
 * Gated behind analytics consent (AC-2). Returns null if consent not granted
 * or cookie is not present.
 */
export const getGAClientId = (): string | null => {
  if (!cookieService.hasAnalyticsConsent()) {
    return null;
  }

  try {
    const gaCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_ga="));
    if (gaCookie) {
      const value = gaCookie.split("=")[1];
      const parts = value.split(".");
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Hook for Google Tag Manager event tracking
 *
 * Usage:
 * const { trackEvent, trackConversion } = useGTM();
 *
 * // Track custom event
 * trackEvent('button_click', { button_name: 'subscribe' });
 *
 * // Track conversion
 * trackConversion('subscription_complete', { plan: 'pro' });
 */
export const useGTM = () => {
  const location = useLocation();
  const prevPathRef = useRef<string>("");

  // Track page views on route change
  useEffect(() => {
    if (!isGTMEnabled()) return;

    // Avoid duplicate tracking on initial render
    if (prevPathRef.current === location.pathname + location.search) {
      return;
    }
    prevPathRef.current = location.pathname + location.search;

    pushToDataLayer({
      event: PAGE_VIEW_EVENT,
      page_path: location.pathname,
      page_search: location.search,
      page_title: document.title,
    });
  }, [location]);

  // Track custom event
  const trackEvent = useCallback(
    (eventName: string, params?: GTMEventParams): void => {
      pushToDataLayer({
        event: eventName,
        ...params,
      });
    },
    []
  );

  // Track conversion events (same as trackEvent but semantically different)
  const trackConversion = useCallback(
    (conversionName: string, params?: GTMEventParams): void => {
      pushToDataLayer({
        event: conversionName,
        conversion: true,
        ...params,
      });
    },
    []
  );

  // Set user properties
  const setUserProperties = useCallback(
    (properties: GTMEventParams): void => {
      pushToDataLayer({
        event: "set_user_properties",
        user_properties: properties,
      });
    },
    []
  );

  return {
    trackEvent,
    trackConversion,
    setUserProperties,
    isEnabled: isGTMEnabled(),
  };
};

/**
 * Standalone function for tracking events outside of React components.
 * Useful for tracking in API calls, utilities, etc.
 *
 * @internal WARNING: Consent is checked automatically. Events are blocked silently
 * (with console.warn in dev) only when the user EXPLICITLY declined analytics consent;
 * before a consent decision they are queued inertly in the dataLayer (GTM is not
 * loaded pre-consent, see gtm-loader.js).
 * Exception: 'page_view' events are always allowed as essential analytics.
 */
export const gtmTrackEvent = (
  eventName: string,
  params?: GTMEventParams
): void => {
  pushToDataLayer({
    event: eventName,
    ...params,
  });
};

/**
 * Standalone function for tracking conversions outside of React components.
 *
 * @internal WARNING: Consent is checked automatically (same rules as gtmTrackEvent —
 * blocked only on explicit analytics refusal, queued inertly while undecided).
 */
export const gtmTrackConversion = (
  conversionName: string,
  params?: GTMEventParams
): void => {
  pushToDataLayer({
    event: conversionName,
    conversion: true,
    ...params,
  });
};
